let sharedSingleCardAudio: HTMLAudioElement | null = null;
const cardAudioInstances = new Set<HTMLAudioElement>();

export const TAAS_CARD_AUDIO_ATTR = "data-taas-card-audio";

type CardAudioStateListener = (audio: HTMLAudioElement | null) => void;
const cardAudioStateListeners = new Set<CardAudioStateListener>();
let activeCardAudioElement: HTMLAudioElement | null = null;

const notifyCardAudioState = () => {
  cardAudioStateListeners.forEach((listener) => {
    listener(activeCardAudioElement);
  });
};

export const subscribeCardAudioState = (
  listener: CardAudioStateListener
): (() => void) => {
  cardAudioStateListeners.add(listener);
  listener(activeCardAudioElement);
  return () => {
    cardAudioStateListeners.delete(listener);
  };
};

export const getActiveCardAudio = (): HTMLAudioElement | null =>
  activeCardAudioElement;

const bindCardAudioLifecycle = (audio: HTMLAudioElement) => {
  const boundKey = "__taasCardAudioBound";
  if ((audio as HTMLAudioElement & { [boundKey]?: boolean })[boundKey]) {
    return;
  }
  (audio as HTMLAudioElement & { [boundKey]?: boolean })[boundKey] = true;

  const markActive = () => {
    activeCardAudioElement = audio;
    notifyCardAudioState();
  };

  audio.addEventListener("play", markActive);
  audio.addEventListener("pause", () => {
    if (activeCardAudioElement === audio) {
      notifyCardAudioState();
    }
  });
  audio.addEventListener("timeupdate", () => {
    if (activeCardAudioElement === audio) {
      notifyCardAudioState();
    }
  });
  audio.addEventListener("loadedmetadata", () => {
    if (activeCardAudioElement === audio) {
      notifyCardAudioState();
    }
  });
  audio.addEventListener("ended", () => {
    if (activeCardAudioElement === audio) {
      activeCardAudioElement = null;
      notifyCardAudioState();
    }
  });
};

export const getSharedSingleCardAudio = (): HTMLAudioElement | null => {
  if (typeof window === "undefined") return null;
  if (!sharedSingleCardAudio) {
    sharedSingleCardAudio = new Audio();
    sharedSingleCardAudio.preload = "metadata";
    bindCardAudioLifecycle(sharedSingleCardAudio);
  }
  return sharedSingleCardAudio;
};

export const createCardAudioInstance = (): HTMLAudioElement => {
  const audio = new Audio();
  audio.preload = "metadata";
  registerCardAudioElement(audio);
  return audio;
};

/** Track an existing `<audio>` element (e.g. rendered in JSX per experience page). */
export const registerCardAudioElement = (audio: HTMLAudioElement) => {
  if (!cardAudioInstances.has(audio)) {
    cardAudioInstances.add(audio);
    bindCardAudioLifecycle(audio);
  }
};

/** Pause and unregister without clearing `src` (safe for React-managed `<audio>`). */
export const releaseCardAudioElement = (audio: HTMLAudioElement) => {
  cardAudioInstances.delete(audio);
  if (activeCardAudioElement === audio) {
    activeCardAudioElement = null;
    notifyCardAudioState();
  }
  audio.pause();
};

export const disposeCardAudioInstance = (audio: HTMLAudioElement) => {
  releaseCardAudioElement(audio);

  // Hand off only when leaving the card entirely (no sibling players left).
  if (!audio.paused && !audio.ended && cardAudioInstances.size === 0) {
    handoffToSharedSingleCardAudio(audio);
    return;
  }

  audio.removeAttribute("src");
  audio.load();
};

export const normalizeCardAudioSrc = (src: string) => {
  if (!src) return "";
  if (typeof window === "undefined") return src;
  try {
    return new URL(src, window.location.href).href;
  } catch {
    return src;
  }
};

export const ensureCardAudioSource = (
  audio: HTMLAudioElement,
  nextUrl: string
): boolean => {
  if (!nextUrl) return false;
  const normalizedNext = normalizeCardAudioSrc(nextUrl);
  const normalizedCurrent = normalizeCardAudioSrc(
    audio.currentSrc || audio.src || ""
  );
  if (normalizedCurrent === normalizedNext) return false;
  audio.src = nextUrl;
  audio.load();
  return true;
};

/** Wait until the element can play (needed for AAC / slow CDN metadata). */
export const playCardAudioWhenReady = (
  audio: HTMLAudioElement,
  nextUrl: string
): Promise<void> => {
  ensureCardAudioSource(audio, nextUrl);

  if (audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
    return audio.play().then(() => undefined);
  }

  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      cleanup();
      audio.play().then(resolve).catch(reject);
    }, 12000);

    const onReady = () => {
      cleanup();
      audio.play().then(resolve).catch(reject);
    };

    const onError = () => {
      cleanup();
      reject(new Error("Audio failed to load"));
    };

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      audio.removeEventListener("canplay", onReady);
      audio.removeEventListener("loadedmetadata", onReady);
      audio.removeEventListener("error", onError);
    };

    audio.addEventListener("canplay", onReady);
    audio.addEventListener("loadedmetadata", onReady);
    audio.addEventListener("error", onError);
  });
};

export const pauseOtherCardAudioInstances = (current: HTMLAudioElement) => {
  cardAudioInstances.forEach((el) => {
    if (el !== current && !el.paused) {
      el.pause();
    }
  });

  if (typeof document !== "undefined") {
    document.querySelectorAll(`audio[${TAAS_CARD_AUDIO_ATTR}]`).forEach((el) => {
      if (el !== current && el instanceof HTMLAudioElement && !el.paused) {
        el.pause();
      }
    });
  }

  if (
    sharedSingleCardAudio &&
    sharedSingleCardAudio !== current &&
    !sharedSingleCardAudio.paused
  ) {
    sharedSingleCardAudio.pause();
  }
};

export const handoffToSharedSingleCardAudio = (source: HTMLAudioElement) => {
  if (typeof window === "undefined") return;

  const shared = getSharedSingleCardAudio();
  if (!shared || shared === source) return;
  if (source.paused || source.ended) return;

  const src = source.currentSrc || source.src;
  if (!src) return;

  const normalizeSrc = (value: string) => {
    if (!value) return "";
    try {
      return new URL(value, window.location.href).href;
    } catch {
      return value;
    }
  };

  const nextSrc = normalizeSrc(src);
  const currentSharedSrc = normalizeSrc(shared.currentSrc || shared.src || "");

  if (currentSharedSrc !== nextSrc) {
    shared.src = src;
    shared.load();
  }

  shared.currentTime = source.currentTime;
  shared.dataset.singleCardTitle = source.dataset.singleCardTitle || "";
  shared.dataset.singleCardArtist = source.dataset.singleCardArtist || "";
  shared.dataset.singleCardArtwork = source.dataset.singleCardArtwork || "";

  source.pause();
  void shared.play().catch(() => {});
};

/** Pause every other TAASCARD preview `<audio>` so only one plays at a time. */
export const pauseOtherTaasCardAudios = (current: HTMLAudioElement) => {
  pauseOtherCardAudioInstances(current);
};

export const pauseActiveCardAudio = () => {
  const active = activeCardAudioElement;
  if (active) {
    active.pause();
    active.currentTime = 0;
    activeCardAudioElement = null;
    notifyCardAudioState();
    return;
  }

  const shared = getSharedSingleCardAudio();
  if (shared) {
    shared.pause();
    shared.currentTime = 0;
  }
};
